import React, { useState, useEffect } from "react";
import { BrainCircuit, CheckCircle2, XCircle, Lightbulb, Search, BookOpen, Clock } from "lucide-react";

export default function LoreDashboard() {
  const [lores, setLores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchLores();
  }, []);

  const fetchLores = async () => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch("/api/ai/lore", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLores(data.lores || []);
      }
    } catch (error) {
      console.error("Error fetching lore:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "POST_MORTEM": return <BrainCircuit className="w-5 h-5 text-indigo-500" />;
      case "RULE": return <BookOpen className="w-5 h-5 text-blue-500" />;
      case "INSIGHT": return <Lightbulb className="w-5 h-5 text-amber-500" />;
      default: return <BrainCircuit className="w-5 h-5 text-gray-500" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "POST_MORTEM": return "bg-indigo-50 border-indigo-200 text-indigo-700";
      case "RULE": return "bg-blue-50 border-blue-200 text-blue-700";
      case "INSIGHT": return "bg-amber-50 border-amber-200 text-amber-700";
      default: return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "POST_MORTEM": return "Опыт (Post-Mortem)";
      case "RULE": return "Правило";
      case "INSIGHT": return "Инсайт";
      default: return category;
    }
  };

  const filteredLores = lores.filter(lore => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (lore.contentText || "").toLowerCase().includes(query) || 
           (lore.category || "").toLowerCase().includes(query);
  });

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <BrainCircuit className="w-6 h-6 text-indigo-600" />
            База знаний (AI Lore)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Сжатый опыт компании, извлеченный искусственным интеллектом из закрытых задач.
          </p>
        </div>
        
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="Поиск по легаси..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-shadow"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <BrainCircuit className="w-8 h-8 animate-pulse mb-3" />
          <p className="text-sm">Синхронизация с RAG-хранилищем...</p>
        </div>
      ) : filteredLores.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center bg-white border border-gray-100 rounded-2xl shadow-sm">
          <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mb-3">
            <BookOpen className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">База знаний пуста</h3>
          <p className="text-sm text-gray-500 max-w-sm">
            Закрывайте задачи на канбан-доске, чтобы ИИ анализировал их и формировал базу опыта компании.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLores.map((lore) => (
            <div key={lore.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="shrink-0 mt-1">
                  {getCategoryIcon(lore.category)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(lore.category)}`}>
                      {getCategoryLabel(lore.category)}
                    </span>
                    <span className="flex items-center text-xs text-gray-500">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      {new Date(lore.createdAt).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
                    </span>
                  </div>
                  
                  <div className="prose prose-sm max-w-none text-gray-700">
                    <p className="whitespace-pre-wrap leading-relaxed">{lore.contentText || lore.summary}</p>
                  </div>

                  {lore.contentJson && typeof lore.contentJson === "object" && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {lore.contentJson.approved_decisions && lore.contentJson.approved_decisions.length > 0 && (
                        <div className="bg-green-50/50 border border-green-100 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-green-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Успешные решения
                          </h4>
                          <ul className="space-y-1.5">
                            {lore.contentJson.approved_decisions.map((dec, idx) => (
                              <li key={idx} className="text-sm text-green-700 flex items-start gap-1.5">
                                <span className="mt-1 w-1 h-1 shrink-0 rounded-full bg-green-500" />
                                <span>{dec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {lore.contentJson.rejected_ideas && lore.contentJson.rejected_ideas.length > 0 && (
                        <div className="bg-red-50/50 border border-red-100 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-red-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                            <XCircle className="w-3.5 h-3.5" /> Отклоненные идеи
                          </h4>
                          <ul className="space-y-1.5">
                            {lore.contentJson.rejected_ideas.map((idea, idx) => (
                              <li key={idx} className="text-sm text-red-700 flex items-start gap-1.5">
                                <span className="mt-1 w-1 h-1 shrink-0 rounded-full bg-red-400" />
                                <span>{idea}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
